import {
  type AIPhoneLineOwnerEnum,
  type AIPhoneLineTypeEnum,
  type EnrichedAIPhoneLineConfig,
  phoneComponents,
} from '~types'

export const MAX_GREETING_LENGTH = 1000

export const getPhoneLineTypeDisplay: (type: AIPhoneLineTypeEnum) => string = (
  type: AIPhoneLineTypeEnum,
) => {
  switch (type) {
    case 'ELEVEN_LABS':
      return 'Studio'
    case 'PHONIC':
      return 'Conversational'
    default:
      return 'Unknown'
  }
}

export const getPhoneLineOwnerTypeDisplay: (type: AIPhoneLineOwnerEnum) => string = (
  type: AIPhoneLineOwnerEnum,
) => {
  switch (type) {
    case 'CUSTOMER':
      return 'Customer'
    case 'DEMO':
      return 'Demo'
  }
}

export interface IPhoneNumber {
  phoneNumber: string
}

export const getPhoneNumber = (phoneString: string): IPhoneNumber | undefined => {
  const [cleanedNumber] = phoneComponents(phoneString)
  if (cleanedNumber && cleanedNumber.length >= 10) {
    return { phoneNumber: cleanedNumber }
  }
}

// PhoneEntryBox emits the extension inline as "8015551234x8000"
export const transferLineFields = (
  phoneEntryState: string,
): Pick<EnrichedAIPhoneLineConfig, 'default_transfer_number' | 'default_transfer_extension'> => {
  const [number, extension] = phoneComponents(phoneEntryState)
  return {
    default_transfer_number: number,
    default_transfer_extension: extension ? parseInt(extension, 10) : null,
  }
}

export const validatePhoneSettings = ({
  number,
  isValidPhone,
  defaultTransferLine,
  isValidDefaultTransferPhone,
  defaultGreetingMessage,
}: {
  number: string
  isValidPhone: boolean
  defaultTransferLine: string
  isValidDefaultTransferPhone: boolean
  defaultGreetingMessage: string
}): string | null => {
  if (!number) return 'Phone number is required'
  if (!isValidPhone) return 'Invalid phone number format'
  if (defaultTransferLine.trim() !== '' && !isValidDefaultTransferPhone) {
    return 'Invalid default transfer phone number format'
  }
  if (defaultGreetingMessage.length > MAX_GREETING_LENGTH) {
    return `Greeting message must be ${MAX_GREETING_LENGTH.toLocaleString()} characters or fewer`
  }
  return null
}

export const validateScheduleSettings = (
  phoneLineConfigurations: EnrichedAIPhoneLineConfig[],
  phoneLineHolidays: EnrichedAIPhoneLineConfig[],
): string | null => {
  const allConfigs = [...phoneLineConfigurations, ...phoneLineHolidays]
  for (let i = 0; i < allConfigs.length; i++) {
    const config = allConfigs[i]
    if (!config.name || !config.name.trim()) {
      return `Configuration ${i + 1}: Name is required`
    }
    const hasInactiveDates = config.inactive_dates && config.inactive_dates.length > 0
    if (!hasInactiveDates && (!config.start_time || !config.end_time)) {
      return `Configuration ${i + 1}: Start and end times are required`
    }
    if (
      !hasInactiveDates &&
      (!Array.isArray(config.days_of_week) || config.days_of_week.length === 0)
    ) {
      return `Configuration ${i + 1}: At least one day must be selected`
    }
    if (
      config.default_transfer_number &&
      config.default_transfer_number.trim() !== '' &&
      !getPhoneNumber(config.default_transfer_number)
    ) {
      return `Configuration ${i + 1}: Invalid transfer phone number format`
    }
    if ((config.greeting_message?.length ?? 0) > MAX_GREETING_LENGTH) {
      return `Configuration ${i + 1}: Greeting message must be ${MAX_GREETING_LENGTH.toLocaleString()} characters or fewer`
    }
  }
  return null
}
